import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { agentApi } from '../services/api';
import { Agent } from '../types/api';
import AddAgentModal from './AddAgentModal';
import ImageHoverPreview from './ImageHoverPreview';

interface MyAgentsProps {
  isAuthenticated: boolean;
  onLoginRequired: () => void;
}

// 扩展Agent类型以支持购买信息
interface PurchasedAgent extends Agent {
  isPurchased?: boolean;
  purchasedAt?: string;
}

const MyAgents: React.FC<MyAgentsProps> = ({ isAuthenticated, onLoginRequired }) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [purchasedAgents, setPurchasedAgents] = useState<PurchasedAgent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'created' | 'purchased'>('created');

  // 加载我的智能体和已购买的智能体
  const loadMyAgents = useCallback(async () => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // 并行加载创建的和购买的智能体
      const [myResult, purchasedResult] = await Promise.all([
        agentApi.getMyAgents({
        search: searchQuery || undefined,
        limit: 1000
        }),
        agentApi.getPurchasedAgents()
      ]);

      if (myResult.success) {
        setAgents(myResult.data || []);
      }
      
      if (purchasedResult.success) {
        setPurchasedAgents(purchasedResult.data || []);
      }
    } catch (error) {
      console.error('加载智能体失败:', error);
      toast.error('加载失败，请重试');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, searchQuery]);

  useEffect(() => {
    loadMyAgents();
  }, [loadMyAgents]);

  // 删除智能体
  const handleDelete = async (id: number) => {
    if (!window.confirm('确定要删除这个智能体吗？此操作不可撤销。')) {
      return;
    }

    setDeletingId(id);
    try {
      const result = await agentApi.deleteAgent(id);
      if (result.success) {
        toast.success('删除成功');
        setAgents(agents.filter(a => a.id !== id));
      }
    } catch (error: any) {
      toast.error(error.message || '删除失败');
    } finally {
      setDeletingId(null);
    }
  };

  // 打开智能体链接
  const handleOpenAgent = (url: string) => {
    window.open(url, '_blank');
  };

  // 未登录状态
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-20 pb-12">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-md mx-auto text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center">
              <i className="fas fa-robot text-3xl text-primary"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              登录后管理您的智能体
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              创建并分享您的 TRAE 智能体到市场
            </p>
            <button
              onClick={onLoginRequired}
              className="px-8 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              <i className="fas fa-sign-in-alt mr-2"></i>
              立即登录
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-20 pb-12">
      <div className="container mx-auto px-4 sm:px-6">
        {/* 页面标题 */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              <i className="fas fa-robot text-primary mr-3"></i>
              我的智能体
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              管理您分享的 TRAE 智能体
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center shadow-sm"
          >
            <i className="fas fa-plus mr-2"></i>
            添加智能体
          </button>
        </div>

        {/* 标签页切换 */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => setActiveTab('created')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'created'
                ? 'bg-primary text-white'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <i className="fas fa-pencil-alt mr-2"></i>
            我创建的 ({agents.length})
          </button>
          <button
            onClick={() => setActiveTab('purchased')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'purchased'
                ? 'bg-primary text-white'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <i className="fas fa-shopping-bag mr-2"></i>
            我购买的 ({purchasedAgents.length})
          </button>
        </div>

        {/* 搜索框 - 仅在"我创建的"标签页显示 */}
        {activeTab === 'created' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 mb-6">
          <div className="relative">
            <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="搜索智能体..."
              className="w-full pl-11 pr-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            />
          </div>
        </div>
        )}

        {/* 智能体列表 */}
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : activeTab === 'created' && agents.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
              <i className="fas fa-robot text-4xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              还没有智能体
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              添加您的第一个 TRAE 智能体，分享给更多人
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              <i className="fas fa-plus mr-2"></i>
              添加智能体
            </button>
          </div>
        ) : activeTab === 'purchased' && purchasedAgents.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
              <i className="fas fa-shopping-bag text-4xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              还没有购买的智能体
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              去智能体市场发现并购买精彩的智能体
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {(activeTab === 'created' ? agents : purchasedAgents).map(agent => {
                const isPurchased = activeTab === 'purchased';
                return (
                <motion.div
                    key={`${isPurchased ? 'p-' : ''}${agent.id}`}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
                >
                  <div className="flex flex-col md:flex-row">
                    {/* 左侧：缩略图 */}
                      <div className="md:w-48 flex-shrink-0 relative group">
                      {agent.images && agent.images.length > 0 ? (
                        <div className="aspect-video md:aspect-square w-full h-full relative">
                          <img
                            src={agent.images[0]}
                            alt={agent.name}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                          {/* 悬停预览层 */}
                          <div className="absolute inset-0 z-10">
                            <ImageHoverPreview
                              imageUrl={agent.images[0]}
                              alt={agent.name}
                              thumbnailClassName="w-full h-full opacity-0"
                              previewSize={400}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="aspect-video md:aspect-square w-full h-full bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center">
                          <i className="fas fa-robot text-3xl text-primary/50"></i>
                        </div>
                      )}
                        {/* 已购买标签 */}
                        {isPurchased && (
                          <div className="absolute top-2 left-2 px-2 py-1 bg-emerald-500 text-white text-xs font-medium rounded-full z-20">
                            <i className="fas fa-check mr-1"></i>
                            已购买
                          </div>
                        )}
                    </div>

                    {/* 右侧：信息 */}
                    <div className="flex-1 p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                            {agent.name}
                          </h3>
                          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                              {isPurchased ? (
                                <>
                                  <span className="flex items-center text-primary">
                                    <i className="fas fa-user mr-1"></i>
                                    {agent.author || '未知作者'}
                                  </span>
                                  <span>•</span>
                                  <span>购买于 {new Date((agent as PurchasedAgent).purchasedAt || agent.createdAt).toLocaleDateString()}</span>
                                </>
                              ) : (
                                <>
                            {agent.isPublic ? (
                              <span className="flex items-center text-green-600">
                                <i className="fas fa-globe mr-1"></i>
                                公开
                              </span>
                            ) : (
                              <span className="flex items-center text-gray-400">
                                <i className="fas fa-lock mr-1"></i>
                                私密
                              </span>
                            )}
                            <span>•</span>
                            <span>{new Date(agent.createdAt).toLocaleDateString()}</span>
                                </>
                              )}
                            {agent.hotness !== undefined && agent.hotness > 0 && (
                              <>
                                <span>•</span>
                                <span className="text-orange-500">
                                  <i className="fas fa-fire mr-1"></i>
                                  {agent.hotness}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* 行业标签 */}
                      {agent.industryTags && agent.industryTags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {agent.industryTags.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* 描述 */}
                      {agent.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-4">
                          {agent.description}
                        </p>
                      )}

                      {/* 操作按钮 */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleOpenAgent(agent.url)}
                          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm flex items-center"
                        >
                          <i className="fas fa-external-link-alt mr-1.5"></i>
                            打开使用
                        </button>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(agent.url);
                            toast.success('链接已复制');
                          }}
                          className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm flex items-center"
                        >
                          <i className="fas fa-copy mr-1.5"></i>
                          复制
                        </button>
                          {/* 编辑和删除按钮仅对自己创建的智能体显示 */}
                          {!isPurchased && (
                            <>
                        <button
                          onClick={() => setEditingAgent(agent)}
                          className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm flex items-center"
                        >
                          <i className="fas fa-edit mr-1.5"></i>
                          编辑
                        </button>
                        <button
                          onClick={() => handleDelete(agent.id)}
                          disabled={deletingId === agent.id}
                          className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm flex items-center disabled:opacity-50"
                        >
                          {deletingId === agent.id ? (
                            <i className="fas fa-spinner fa-spin mr-1.5"></i>
                          ) : (
                            <i className="fas fa-trash mr-1.5"></i>
                          )}
                          删除
                        </button>
                            </>
                          )}
                      </div>
                    </div>
                  </div>
                </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}

        {/* 添加/编辑弹窗 */}
        <AddAgentModal
          isOpen={showAddModal || !!editingAgent}
          onClose={() => {
            setShowAddModal(false);
            setEditingAgent(null);
          }}
          onSuccess={loadMyAgents}
          editingAgent={editingAgent}
        />
      </div>
    </div>
  );
};

export default MyAgents;

